
/**
 * AppConfig - Centralized Configuration Manager
 * 
 * Loads connection settings (Odoo URL, DB, etc.) at RUNTIME from the
 * Worker's /config endpoint.  This means:
 *   - No secrets are baked into the JS bundle at build time.
 *   - Changing a value in Cloudflare Worker env vars takes effect immediately.
 *   - Service-account credentials never leave the server.
 *
 * Non-sensitive UI preferences (language, theme, remember-me) are still
 * persisted in localStorage as before.
 */

/**
 * RuntimeConfig - All settings served from Cloudflare Worker env vars.
 * No hardcoded values. Everything is controlled from Cloudflare dashboard.
 */
export interface RuntimeConfig {
  odooUrl: string;
  odooDb: string;
  appNameAr: string;
  appNameEn: string;
  phoneLookupUser: string;
  phoneLookupDb: string;
  sessionTimeout: string;
  defaultLang: string;
  defaultTheme: string;
  downloadUrl: string;
  supportEmail: string;
  supportPhone: string;
  supportWhatsapp: string;
}

export class AppConfig {
  private static instance: AppConfig;

  /** localStorage keys for UI preferences only (non-sensitive) */
  static KEYS = {
    DEFAULT_LANG: 'app_config_default_lang',
    DEFAULT_THEME: 'app_config_default_theme',
    LOGIN_IDENTIFIER: 'app_config_last_login_identifier',
    REMEMBER_ME: 'app_config_remember_me',
  } as const;

  /** Runtime config fetched from Worker /config endpoint */
  private runtimeConfig: RuntimeConfig | null = null;
  private configPromise: Promise<RuntimeConfig> | null = null;

  /** The backend base URL (without /api). Auto-detected from origin. */
  private backendBaseUrl: string;

  private constructor() {
    // Always use current origin as backend base (Cloudflare Pages Functions serve all endpoints)
    // In dev, Vite proxy forwards /api → localhost:8788 (wrangler pages dev)
    this.backendBaseUrl = window.location.origin.replace(/\/api\/?$/, '');
  }

  // ────────────────────────────────────────────
  //  Runtime Config (fetched from Worker /config)
  // ────────────────────────────────────────────

  /** Fetch runtime config from the Worker. Cached after first call. */
  async loadConfig(): Promise<RuntimeConfig> {
    if (this.runtimeConfig) return this.runtimeConfig;

    if (!this.configPromise) {
      this.configPromise = this._fetchConfig();
    }
    return this.configPromise;
  }

  private async _fetchConfig(): Promise<RuntimeConfig> {
    const url = `${this.backendBaseUrl}/config`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
      const data = await res.json() as RuntimeConfig;
      this.runtimeConfig = data;
      return data;
    } catch (err) {
      console.error('[AppConfig] Failed to load runtime config:', err);
      // Fallback with empty values – app shows config error
      this.runtimeConfig = {
        odooUrl: '',
        odooDb: '',
        appNameAr: 'بوابة الدعم الفني',
        appNameEn: 'MR | Portal',
        phoneLookupUser: '',
        phoneLookupDb: '',
        sessionTimeout: '3600000',
        defaultLang: 'ar',
        defaultTheme: 'light',
        downloadUrl: '',
        supportEmail: '',
        supportPhone: '',
        supportWhatsapp: '',
      };
      return this.runtimeConfig;
    }
  }

  /** Check whether the runtime config has been loaded */
  get isConfigLoaded(): boolean {
    return this.runtimeConfig !== null;
  }

  /** Check whether the runtime config has essential values */
  get isConfigured(): boolean {
    return !!(this.runtimeConfig?.odooUrl && this.runtimeConfig?.odooDb);
  }

  // ────────────────────────────────────────────
  //  Convenience Getters (all from runtime config)
  // ────────────────────────────────────────────

  /** Odoo instance URL – controlled by ODOO_URL env var */
  get odooUrl(): string { return this.runtimeConfig?.odooUrl || ''; }

  /** Odoo database name – controlled by ODOO_DB env var */
  get odooDb(): string { return this.runtimeConfig?.odooDb || ''; }

  /** Backend API URL (Pages Functions /api base) */
  get backendUrl(): string {
    const base = this.backendBaseUrl;
    return base ? `${base}/api` : '/api';
  }

  /** Backend base URL (without /api) */
  get baseUrl(): string { return this.backendBaseUrl; }

  /** App name in Arabic – controlled by APP_NAME_AR env var */
  get appNameAr(): string { return this.runtimeConfig?.appNameAr || 'بوابة الدعم الفني'; }

  /** App name in English – controlled by APP_NAME_EN env var */
  get appNameEn(): string { return this.runtimeConfig?.appNameEn || 'MR | Portal'; }

  /** Phone lookup Odoo user – controlled by PHONE_LOOKUP_USER env var */
  get phoneLookupUser(): string { return this.runtimeConfig?.phoneLookupUser || ''; }

  /** Phone lookup Odoo DB – controlled by PHONE_LOOKUP_DB env var */
  get phoneLookupDb(): string { return this.runtimeConfig?.phoneLookupDb || this.odooDb; }

  /** Session timeout in ms – controlled by SESSION_TIMEOUT env var */
  get sessionTimeout(): number {
    return parseInt(this.runtimeConfig?.sessionTimeout || '3600000') || 3600000;
  }

  /** Default language – controlled by DEFAULT_LANG env var (can be overridden by user) */
  get defaultLang(): string {
    return this._getLocal(AppConfig.KEYS.DEFAULT_LANG) || this.runtimeConfig?.defaultLang || 'ar';
  }

  /** Default theme – controlled by DEFAULT_THEME env var (can be overridden by user) */
  get defaultTheme(): string {
    return this._getLocal(AppConfig.KEYS.DEFAULT_THEME) || this.runtimeConfig?.defaultTheme || 'light';
  }

  /** Download URL – controlled by DOWNLOAD_URL env var */
  get downloadUrl(): string { return this.runtimeConfig?.downloadUrl || ''; }

  /** Support email – controlled by SUPPORT_EMAIL env var */
  get supportEmail(): string { return this.runtimeConfig?.supportEmail || ''; }

  /** Support phone – controlled by SUPPORT_PHONE env var */
  get supportPhone(): string { return this.runtimeConfig?.supportPhone || ''; }

  /** Support WhatsApp – controlled by SUPPORT_WHATSAPP env var */
  get supportWhatsapp(): string { return this.runtimeConfig?.supportWhatsapp || ''; }

  // ────────────────────────────────────────────
  //  User preferences (localStorage only)
  // ────────────────────────────────────────────

  get lastLoginIdentifier(): string { return this._getLocal(AppConfig.KEYS.LOGIN_IDENTIFIER) || ''; }
  set lastLoginIdentifier(value: string) { this._setLocal(AppConfig.KEYS.LOGIN_IDENTIFIER, value); }

  get rememberMe(): boolean { return this._getLocal(AppConfig.KEYS.REMEMBER_ME) === 'true'; }
  set rememberMe(value: boolean) { this._setLocal(AppConfig.KEYS.REMEMBER_ME, String(value)); }

  /** Override default language (user preference) */
  set defaultLang(lang: string) { this._setLocal(AppConfig.KEYS.DEFAULT_LANG, lang); }

  /** Override default theme (user preference) */
  set defaultTheme(theme: string) { this._setLocal(AppConfig.KEYS.DEFAULT_THEME, theme); }

  // ────────────────────────────────────────────
  //  localStorage helpers
  // ────────────────────────────────────────────

  private _getLocal(key: string): string {
    try { return localStorage.getItem(key) || ''; } catch { return ''; }
  }

  private _setLocal(key: string, value: string): void {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch { /* noop */ }
  }

  /** Reset a config key */
  reset(key: string): void {
    try { localStorage.removeItem(key); } catch { /* noop */ }
  }

  /** Reset all user preference keys */
  resetAll(): void {
    Object.values(AppConfig.KEYS).forEach(key => {
      try { localStorage.removeItem(key); } catch { /* noop */ }
    });
  }

  /** Get all config entries for the settings page */
  getAllConfigs(): Array<{ key: string; label: string; value: string; source: string }> {
    return [
      { key: 'ODOO_URL', label: 'Odoo URL', value: this.odooUrl, source: 'Cloudflare Env' },
      { key: 'ODOO_DB', label: 'Database', value: this.odooDb, source: 'Cloudflare Env' },
      { key: 'PHONE_LOOKUP_USER', label: 'Phone Lookup User', value: this.phoneLookupUser, source: 'Cloudflare Env' },
      { key: 'PHONE_LOOKUP_DB', label: 'Phone Lookup DB', value: this.phoneLookupDb, source: 'Cloudflare Env' },
      { key: 'APP_NAME_AR', label: 'App Name (AR)', value: this.appNameAr, source: 'Cloudflare Env' },
      { key: 'APP_NAME_EN', label: 'App Name (EN)', value: this.appNameEn, source: 'Cloudflare Env' },
      { key: 'SESSION_TIMEOUT', label: 'Session Timeout', value: String(this.sessionTimeout), source: 'Cloudflare Env' },
      { key: 'DEFAULT_LANG', label: 'Default Language', value: this.defaultLang, source: 'Cloudflare Env' },
      { key: 'DEFAULT_THEME', label: 'Default Theme', value: this.defaultTheme, source: 'Cloudflare Env' },
      { key: 'DOWNLOAD_URL', label: 'Download URL', value: this.downloadUrl, source: 'Cloudflare Env' },
      { key: 'SUPPORT_EMAIL', label: 'Support Email', value: this.supportEmail, source: 'Cloudflare Env' },
      { key: 'SUPPORT_PHONE', label: 'Support Phone', value: this.supportPhone, source: 'Cloudflare Env' },
      { key: 'SUPPORT_WHATSAPP', label: 'Support WhatsApp', value: this.supportWhatsapp, source: 'Cloudflare Env' },
    ];
  }

  /** Singleton */
  static getInstance(): AppConfig {
    if (!AppConfig.instance) {
      AppConfig.instance = new AppConfig();
    }
    return AppConfig.instance;
  }
}

/** Global app config instance */
export const appConfig = AppConfig.getInstance();
